package com.swetdot.swetgram.ui.auth

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.lifecycle.compose.collectAsStateWithLifecycle

/**
 * The signed-out flow: sign in, register, and recover a forgotten password.
 *
 * Verification is NOT a step here. Registration returns a session, so once it succeeds the app
 * is signed in but unverified -- a distinct state that `App` gates on. Keeping it out of this
 * wizard is also what stops a back press from returning to the registration form: the account
 * already exists at that point, and resubmitting it would only produce "username taken"
 * against the user's own account.
 *
 * Recovery IS a step here, and has to be: it is the only branch of the app reachable by someone
 * who cannot sign in. Its two screens live behind one step of this enum because their order is
 * decided by the server's answers, not by navigation -- [RecoveryState] holds that position.
 */
private enum class AuthStep { SignIn, Register, Recovery }

@Composable
fun AuthFlow(viewModel: AuthViewModel) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val recovery by viewModel.recovery.collectAsStateWithLifecycle()
    var step by remember { mutableStateOf(AuthStep.SignIn) }

    // Carried out of the recovery flow and into the sign-in form.
    //
    // SPS §8.5 says only "Password reset uses email recovery" -- it does not say where the user
    // ends up, so the server decides. Probed against the deployed server with a planted code:
    // `resetPassword` answered 204 with a zero-byte body and no header but `X-Request-ID`, so
    // there is no session to land in the app with; the refresh token issued before the reset then
    // answered 401 `session_revoked`, so the account's other sessions are genuinely gone. The
    // flow therefore ends at sign-in, and these two exist so that it does not end there silently
    // and unexplained -- the address the user just proved they own is prefilled, and the notice
    // says plainly what happened.
    //
    // SAVED, not merely remembered. `androidApp` declares no `configChanges`, so the Activity is
    // recreated on a rotation; a plain `remember` would drop both, and the user would land back on
    // sign-in with an empty address field and no explanation of why they were signed out. Strings
    // and Booleans need no custom saver on any Compose Multiplatform target.
    var resetEmail by rememberSaveable { mutableStateOf("") }
    var passwordWasReset by rememberSaveable { mutableStateOf(false) }

    LaunchedEffect(recovery.step) {
        if (recovery.step == RecoveryStep.Done) {
            resetEmail = recovery.email
            passwordWasReset = true
            step = AuthStep.SignIn
            // Read before it is cleared: the next attempt must start from an empty form rather
            // than reopening on a spent code.
            viewModel.startRecovery()
        }
    }

    fun leaveSignIn(destination: AuthStep) {
        // Errors belong to the screen that produced them; carrying a sign-in failure onto
        // another form would be baffling. The reset confirmation goes the same way -- it is
        // about the screen being left, not the one being entered.
        viewModel.clearErrors()
        passwordWasReset = false
        step = destination
    }

    when (step) {
        AuthStep.SignIn -> SignInScreen(
            state = state,
            onSignIn = viewModel::signIn,
            onGoToRegister = { leaveSignIn(AuthStep.Register) },
            onForgotPassword = {
                viewModel.startRecovery()
                leaveSignIn(AuthStep.Recovery)
            },
            initialIdentifier = resetEmail,
            notice = "Your password was changed. Sign in with your new one."
                .takeIf { passwordWasReset },
        )

        AuthStep.Register -> RegisterScreen(
            state = state,
            onRegister = viewModel::register,
            onGoToSignIn = {
                viewModel.clearErrors()
                step = AuthStep.SignIn
            },
        )

        AuthStep.Recovery -> when (recovery.step) {
            RecoveryStep.Email -> ForgotPasswordScreen(
                state = recovery.form,
                onSubmit = viewModel::requestPasswordReset,
                onBack = {
                    viewModel.startRecovery()
                    step = AuthStep.SignIn
                },
                initialEmail = recovery.email,
            )

            // Done is grouped with Code deliberately. The effect above leaves this step, but
            // effects run AFTER the composition that set it, so there is one frame to draw --
            // and the right thing to draw is the screen the user is already looking at. Giving
            // Done its own branch, or folding it in with Email, would flash the address form
            // between "Set new password" and the sign-in screen.
            RecoveryStep.Code, RecoveryStep.Done -> ResetPasswordScreen(
                state = recovery.form,
                email = recovery.email,
                onReset = viewModel::resetPassword,
                // The contract has no resend for password reset; asking again is the resend,
                // and it issues a new code against the same 5-per-hour budget.
                onResend = { viewModel.requestPasswordReset(recovery.email) },
                onUseAnotherEmail = viewModel::restartRecovery,
            )
        }
    }
}
